import React, { useState, useEffect } from 'react';
import './CMSPanel.css';
import ContentTypeBuilder from './ContentTypeBuilder';
import ContentForm from './ContentForm';
import { buildApiUrl } from '../../config/api';

const DeleteConfirmation = ({ isOpen, onClose, onConfirm, contentName }) => {
  if (!isOpen) return null;
  
  return (
    <div className="delete-confirmation-overlay">
      <div className="delete-confirmation-modal">
        <h4>Confirm Deletion</h4>
        <p>Are you sure you want to delete <strong>{contentName}</strong>?</p>
        <p className="delete-warning">This action cannot be undone.</p>
        <div className="delete-confirmation-actions">
          <button 
            className="cancel-delete-button"
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            className="confirm-delete-button"
            onClick={onConfirm}
          >
            Yes, Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// Add a separate confirmation component for content types
const TypeDeleteConfirmation = ({ isOpen, onClose, onConfirm, typeName }) => {
  if (!isOpen) return null;
  
  return (
    <div className="delete-confirmation-overlay">
      <div className="delete-confirmation-modal">
        <h4>Confirm Content Type Deletion</h4>
        <p>Are you sure you want to delete the <strong>{typeName}</strong> content type?</p>
        <p className="delete-warning">This will delete ALL content items of this type and any associated template pages. This action cannot be undone.</p>
        <div className="delete-confirmation-actions">
          <button 
            className="cancel-delete-button"
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            className="confirm-delete-button"
            onClick={onConfirm}
          >
            Yes, Delete Content Type
          </button>
        </div>
      </div>
    </div>
  );
};

const CMSPanel = ({ 
  siteId, 
  contentTypes, 
  contentByType,
  onContentTypeCreate, 
  onContentTypeDelete,
  onContentUpdate,
  assets = [] 
}) => {
  const [selectedType, setSelectedType] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [showTemplates, setShowTemplates] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    contentId: null,
    contentName: ''
  });
  
  // Add a separate state for content type deletion
  const [typeDeleteConfirmation, setTypeDeleteConfirmation] = useState({
    isOpen: false,
    typeId: null,
    typeName: ''
  });

  // Define content type templates
  const contentTypeTemplates = [
    {
      name: "Blog Post",
      icon: "📝",
      description: "For creating blog articles with title, content, author, and featured image.",
      fields: [
        { name: "title", type: "text", required: true, label: "Title" },
        { name: "content", type: "richText", required: true, label: "Content" },
        { name: "author", type: "text", required: false, label: "Author" },
        { name: "featured_image", type: "image", required: false, label: "Featured Image" },
        { name: "publish_date", type: "date", required: false, label: "Publish Date" },
        { name: "tags", type: "text", required: false, label: "Tags" }
      ]
    },
    {
      name: "Product",
      icon: "🛍️",
      description: "For e-commerce products with name, price, description, and images.",
      fields: [
        { name: "name", type: "text", required: true, label: "Product_Name" },
        { name: "price", type: "number", required: true, label: "Price" },
        { name: "description", type: "text", required: true, label: "Description" },
        { name: "images", type: "image", required: false, label: "Product_Images" },
        { name: "sku", type: "text", required: false, label: "SKU" },
        { name: "in_stock", type: "boolean", required: false, label: "In_Stock" },
        { name: "cart_name", type: "cart_name", required: false, label: "Cart_Name", originalType: "cart_name" },
        { name: "cart_price", type: "cart_price", required: false, label: "Cart_Price", originalType: "cart_price" },
        { name: "cart_image", type: "cart_image", required: false, label: "Cart_Image", originalType: "cart_image" }
      ]
    },
    {
      name: "Team Member",
      icon: "👤",
      description: "For team or staff profiles with name, position, bio, and photo.",
      fields: [
        { name: "name", type: "text", required: true, label: "Name" },
        { name: "position", type: "text", required: true, label: "Position" },
        { name: "bio", type: "textarea", required: false, label: "Biography" },
        { name: "photo", type: "image", required: false, label: "Photo" },
        { name: "email", type: "text", required: false, label: "Email" },
        { name: "social_links", type: "text", required: false, label: "Social Links" }
      ]
    },
    {
      name: "FAQ",
      icon: "❓",
      description: "For frequently asked questions with question and answer fields.",
      fields: [
        { name: "question", type: "text", required: true, label: "Question" },
        { name: "answer", type: "richText", required: true, label: "Answer" },
        { name: "category", type: "text", required: false, label: "Category" }
      ]
    },
    {
      name: "Event",
      icon: "📅",
      description: "For events with title, date, location, and description.",
      fields: [
        { name: "title", type: "text", required: true, label: "Event Title" },
        { name: "start_date", type: "date", required: true, label: "Start Date" },
        { name: "end_date", type: "date", required: false, label: "End Date" },
        { name: "location", type: "text", required: true, label: "Location" },
        { name: "description", type: "textarea", required: true, label: "Description" },
        { name: "image", type: "image", required: false, label: "Event Image" }
      ]
    },
    {
      name: "Custom",
      icon: "✨",
      description: "Create a custom content type with your own fields.",
      fields: []
    }
  ];

  useEffect(() => {
    // When a content type is selected, reset the expanded items
    // All items should be collapsed by default, including new items
    if (selectedType && selectedType !== 'new' && !isEditing) {
      // Start with an empty set - all items collapsed
      setExpandedItems(new Set());
    }
  }, [selectedType, isEditing]);

  const handleDeleteType = async (typeId) => {
    try {      
      const response = await fetch(buildApiUrl(`api/cms/types/${typeId}`), {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Show success notification
        const successNotification = document.createElement("div");
        successNotification.className = "notification success";
        successNotification.textContent = "Content type deleted successfully!";
        successNotification.style.cssText = `
          position: fixed;
          right: 20px;
          top: 20px;
          background: #22c55e;
          color: white;
          padding: 10px 20px;
          border-radius: 4px;
          z-index: 1000;
        `;
        document.body.appendChild(successNotification);
        setTimeout(() => successNotification.remove(), 3000);
        
        // Call the parent component's delete handler
        onContentTypeDelete?.(typeId);
        setSelectedType(null);
        
        // Close the confirmation dialog
        closeTypeDeleteConfirmation();
      } else {
        // Show error notification
        const errorNotification = document.createElement("div");
        errorNotification.className = "notification error";
        errorNotification.textContent = data.message || "Error deleting content type";
        errorNotification.style.cssText = `
          position: fixed;
          right: 20px;
          top: 20px;
          background: #ef4444;
          color: white;
          padding: 10px 20px;
          border-radius: 4px;
          z-index: 1000;
        `;
        document.body.appendChild(errorNotification);
        setTimeout(() => errorNotification.remove(), 3000);
        
        console.error('Error response from server:', data);
      }
    } catch (error) {
      console.error('Error deleting content type:', error);
      
      // Show error notification
      const errorNotification = document.createElement("div");
      errorNotification.className = "notification error";
      errorNotification.textContent = "Error deleting content type";
      errorNotification.style.cssText = `
        position: fixed;
        right: 20px;
        top: 20px;
        background: #ef4444;
        color: white;
        padding: 10px 20px;
        border-radius: 4px;
        z-index: 1000;
      `;
      document.body.appendChild(errorNotification);
      setTimeout(() => errorNotification.remove(), 3000);
    }
  };

  const handleEditType = (type) => {
    setSelectedType(type);
    setIsEditing(true);
  };

  const handleUpdateType = async (updatedType) => {
    try {
      // First, get the current content type to compare fields
      const getResponse = await fetch(buildApiUrl(`api/cms/types/${updatedType._id}`));
      const currentData = await getResponse.json();
      
      // Check the structure of the response to find the content type
      let currentType;
      if (currentData.contentType) {
        currentType = currentData.contentType;
      } else if (currentData.success && Array.isArray(currentData.contentTypes) && currentData.contentTypes.length > 0) {
        // If the response has a contentTypes array, find the matching content type
        currentType = currentData.contentTypes.find(type => type._id === updatedType._id);
      } else if (currentData.success && currentData.data) {
        // Some APIs return data in a 'data' field
        currentType = currentData.data;
      }
      
      if (!currentType) {
        // Proceed with update anyway, but we won't be able to detect new fields
        currentType = { fields: [] };
      }
      
      // Find new fields that were added
      const existingFieldNames = currentType.fields.map(field => field.name);
      const newFields = updatedType.fields.filter(field => !existingFieldNames.includes(field.name));
      
      // Update the content type
      const response = await fetch(buildApiUrl(`api/cms/types/${updatedType._id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatedType)
      });
      
      const data = await response.json();
      
      if (data.success) {
        // If there are new fields, update all existing content items to include these fields
        if (newFields.length > 0 && contentByType[updatedType._id]?.length > 0) {
          // Get all content for this type
          const contentItems = contentByType[updatedType._id];
          
          // Update each content item with the new fields (with empty values)
          for (const item of contentItems) {
            if (item.isNew) {
              continue; // Skip new items that haven't been saved yet
            }
            
            const updatedData = { ...item.data };
            
            // Add new fields with empty values
            newFields.forEach(field => {
              if (!updatedData[field.name]) {
                updatedData[field.name] = '';
              }
            });
            
            // Update the content item
            const contentUpdateResponse = await fetch(buildApiUrl(`api/cms/content/${item._id}`), {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                contentTypeId: updatedType._id,
                siteId,
                data: updatedData
              })
            });
            
            await contentUpdateResponse.json();
          }
          
          // Refresh content after updating all items
          onContentUpdate?.(updatedType._id);
        }
        
        // Get the updated content type from the response
        const updatedContentType = data.contentType || data.data || updatedType;
        
        onContentTypeCreate?.(updatedContentType);
        setSelectedType(updatedContentType);
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error updating content type:', error);
    }
  };

  const handleSaveContent = async (contentData, contentId) => {
    try {
      const url = contentId 
        ? buildApiUrl(`api/cms/content/${contentId}`)
        : buildApiUrl('api/cms/content');
      
      const method = contentId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contentTypeId: selectedType._id,
          siteId,
          data: contentData
        })
      });

      const data = await response.json();
      if (data.success) {
        // Notify parent to update content
        onContentUpdate?.(selectedType._id);
        
        // If this was a new item, we don't need to do anything special
        // The ContentForm component will handle switching to view mode
        
        // Collapse the item after saving
        if (contentId) {
          setExpandedItems(prev => {
            const newSet = new Set(prev);
            newSet.delete(contentId);
            return newSet;
          });
        }
      }
    } catch (error) {
      console.error('Error saving content:', error);
    }
  };

  const openDeleteConfirmation = (contentId, contentName) => {
    setDeleteConfirmation({
      isOpen: true,
      contentId,
      contentName
    });
  };

  const closeDeleteConfirmation = () => {
    setDeleteConfirmation({
      isOpen: false,
      contentId: null,
      contentName: ''
    });
  };

  const confirmDelete = async () => {
    try {
      console.log("Confirm Delete Called");
      const response = await fetch(buildApiUrl(`api/cms/content/${deleteConfirmation.contentId}`), {
        method: 'DELETE'
      });

      const data = await response.json();
      if (data.success) {
        onContentUpdate?.(selectedType._id);
        closeDeleteConfirmation();
      }
    } catch (error) {
      console.error('Error deleting content:', error);
    }
  };

  const handleSaveContentType = async (contentType) => {
    try {
      // If this is an existing content type (has _id), update it
      if (contentType && contentType._id) {
        // Call handleUpdateType which has the proper update logic
        await handleUpdateType(contentType);
      } 
      // If this is a new content type, just pass it to the parent
      else if (contentType) {
        onContentTypeCreate?.(contentType);
        setSelectedType(null);
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error saving content type:', error);
    }
  };

  const handleViewData = (type) => {
    setSelectedType(type);
    setIsEditing(false);
  };

  // Add functions to handle content type deletion confirmation
  const openTypeDeleteConfirmation = (typeId, typeName) => {
    setTypeDeleteConfirmation({
      isOpen: true,
      typeId,
      typeName
    });
  };

  const closeTypeDeleteConfirmation = () => {
    setTypeDeleteConfirmation({
      isOpen: false,
      typeId: null,
      typeName: ''
    });
  };

  const confirmTypeDelete = () => {
    handleDeleteType(typeDeleteConfirmation.typeId);
  };

  const handleSelectTemplate = (template) => {
    if (template.name === "Custom") {
      // For custom template, just go to the content type builder with empty fields
      setSelectedType('new');
      setShowTemplates(false);
    } else {
      // For predefined templates, create a new content type with the template fields
      const newContentType = {
        name: template.name,
        fields: template.fields
      };
      
      // Set as the selected type and open in edit mode
      setSelectedType({...newContentType, isTemplate: true});
      setIsEditing(true);
      setShowTemplates(false);
    }
  };

  return (
    <div className="cms-panel">
      <h2>Content Management System</h2>
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmation 
        isOpen={deleteConfirmation.isOpen}
        onClose={closeDeleteConfirmation}
        onConfirm={confirmDelete}
        contentName={deleteConfirmation.contentName}
      />
      
      {/* Type Delete Confirmation Modal */}
      <TypeDeleteConfirmation
        isOpen={typeDeleteConfirmation.isOpen}
        onClose={closeTypeDeleteConfirmation}
        onConfirm={confirmTypeDelete}
        typeName={typeDeleteConfirmation.typeName}
      />
      
      {/* Content Type List or Template Selection */}
      {showTemplates ? (
        <div className="content-types template-selection-container">
          <div className="cms-section-header">
            <h3>Choose a Content Type Template</h3>
            <button 
              onClick={() => setShowTemplates(false)}
              className="cancel-template-button"
            >
              Back to Content Types
            </button>
          </div>
          
          <p className="template-description">Start with a template or create a custom content type from scratch.</p>
          
          <div className="inline-template-grid">
            {contentTypeTemplates.map((template, index) => (
              <div 
                key={index} 
                className="template-card"
                onClick={() => handleSelectTemplate(template)}
              >
                <div className="template-icon">{template.icon}</div>
                <h4>{template.name}</h4>
                <p>{template.description}</p>
                {template.name !== "Custom" && (
                  <span className="field-count">{template.fields.length} fields</span>
                )}
                <button className="select-template-button">
                  {template.name === "Custom" ? "Create Custom" : "Use"}
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="content-types">
          <div className="cms-section-header">
            <h3>Content Types</h3>
            <button onClick={() => {
              setShowTemplates(true);
            }}>Create New Content Type</button>
          </div>
          
          {contentTypes.length === 0 ? (
            <div className="empty-state">
              <p>No content types created yet. Create your first content type to get started!</p>
            </div>
          ) : (
            <ul>
              {contentTypes.map(type => (
                <li key={type._id} className="content-type-item">
                  <div className="content-type-info">
                    <div className="type-details">
                      <span className="type-name">{type.name}</span>
                      <span className="field-count">{type.fields.length} fields</span>
                    </div>
                    <div className="content-type-actions">
                      <button 
                        className="view-data-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewData(type);
                        }}
                      >
                        View Data
                      </button>
                      <button 
                        className="edit-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditType(type);
                        }}
                      >
                        Edit Fields
                      </button>
                      <button 
                        className="delete-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openTypeDeleteConfirmation(type._id, type.name);
                        }}
                        style={{
                          background: "rgba(239, 68, 68, 0.2)",
                          color: "#ef4444",
                          border: "1px solid #ef4444",
                          fontWeight: "500",
                          padding: "6px 12px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "4px",
                          fontSize: "13px"
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Content Type Builder/Editor */}
      {(selectedType === 'new' || isEditing) ? (
        <ContentTypeBuilder
          siteId={siteId}
          contentType={isEditing ? selectedType : null}
          onSave={handleSaveContentType}
          onCancel={() => {
            setSelectedType(null);
            setIsEditing(false);
          }}
          isTemplate={selectedType && selectedType.isTemplate}
        />
      ) : selectedType && !isEditing && selectedType !== 'new' && (
        <div className="content-list">
          <div className="content-list-header">
            <div className="header-left">
              <h3>{selectedType.name} Content</h3>
              <span className="content-count">
                {(contentByType[selectedType._id] || []).length} items
              </span>
            </div>
            <div className="content-list-actions">
              <button 
                className="add-content-button"
                onClick={() => {
                  // Generate a unique ID for the new item
                  const newItemId = `new-${Date.now()}`;
                  
                  const newItem = {
                    isNew: true,
                    data: {},
                    _id: newItemId
                  };
                  
                  const updatedContent = {
                    ...contentByType,
                    [selectedType._id]: [...(contentByType[selectedType._id] || []), newItem]
                  };
                  
                  // Update the content list with the new item
                  onContentUpdate?.(selectedType._id, updatedContent);
                  
                  // Expand the new item automatically
                  setExpandedItems(prev => {
                    const newSet = new Set(prev);
                    newSet.add(newItemId);
                    return newSet;
                  });
                }}
              >
                Add New {selectedType.name}
              </button>
            </div>
          </div>
          
          {(contentByType[selectedType._id] || []).length === 0 ? (
            <div className="empty-state">
              <p>No content items yet. Create your first {selectedType.name} to get started!</p>
            </div>
          ) : (
            <ul>
              {(contentByType[selectedType._id] || []).map((item, index) => (
                <li 
                  key={item._id || `new-${index}`} 
                  className={`content-item ${expandedItems.has(item._id || `new-${index}`) ? 'expanded' : ''}`}
                >
                  <div 
                    className="content-header"
                    onClick={() => {
                      console.log("Header clicked, toggling item:", item._id || `new-${index}`);
                      setExpandedItems(prev => {
                        const newSet = new Set(prev);
                        if (newSet.has(item._id || `new-${index}`)) {
                          newSet.delete(item._id || `new-${index}`);
                        } else {
                          newSet.add(item._id || `new-${index}`);
                        }
                        return newSet;
                      });
                    }}
                  >
                    <div className="content-info">
                      <span className="content-title">
                        {item.isNew ? `New ${selectedType.name}` : (item.data[selectedType.fields[0]?.name] || 'Untitled')}
                      </span>
                    </div>
                    <div className="content-meta">
                      {!item.isNew && (
                        <span className="content-date">
                          {new Date(item.createdAt).toLocaleDateString()}
                        </span>
                      )}
                      {/* Show delete button for both new and existing items */}
                      <button 
                        className="header-delete-button"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent header click event
                          if (item.isNew) {
                            // For new items, just remove them from the list without confirmation
                            const updatedContent = {
                              ...contentByType,
                              [selectedType._id]: (contentByType[selectedType._id] || []).filter(
                                contentItem => contentItem._id !== item._id
                              )
                            };
                            onContentUpdate?.(selectedType._id, updatedContent);
                          } else {
                            // For existing items, show confirmation dialog
                            const contentName = item.data[selectedType.fields[0]?.name] || 'Untitled';
                            openDeleteConfirmation(item._id, contentName);
                          }
                        }}
                        title="Delete content"
                      >
                        <span className="delete-icon">×</span>
                      </button>
                      <span className="expand-indicator">
                        {expandedItems.has(item._id || `new-${index}`) ? '▼' : '▶'}
                      </span>
                    </div>
                  </div>
                  <div className={`content-form cms-panel-content-form ${expandedItems.has(item._id || `new-${index}`) ? 'visible' : ''}`}>
                    <ContentForm
                      contentType={selectedType}
                      content={item.isNew ? null : item}
                      onSave={handleSaveContent}
                      onCancel={() => {
                        if (item.isNew) {
                          // For new items, remove them from the list
                          onContentUpdate?.(selectedType._id);
                        } else {
                          // Collapse the item
                          setExpandedItems(prev => {
                            const newSet = new Set(prev);
                            if (newSet.has(item._id || `new-${index}`)) {
                              newSet.delete(item._id || `new-${index}`);
                            }
                            return newSet;
                          });
                        }
                      }}
                      onDelete={(contentId) => {
                        const contentName = item.data[selectedType.fields[0]?.name] || 'Untitled';
                        openDeleteConfirmation(contentId, contentName);
                      }}
                      assets={assets}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default CMSPanel; 